import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getMyDeclaration, submitDeclaration, getTaxSettings, getTaxComparison } from '../../lib/taxApi';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Plus, Trash2, ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { formatIndianCurrency } from '../../lib/utils';

const MyTaxDeclaration = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [declaration, setDeclaration] = useState({
        financial_year: '',
        regime: 'NEW',
        investments: []
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [comparison, setComparison] = useState(null);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const taxSettings = await getTaxSettings();
            setSettings(taxSettings);

            const myDecl = await getMyDeclaration(taxSettings.current_financial_year);
            if (myDecl) {
                setDeclaration(myDecl);
            } else {
                setDeclaration(prev => ({
                    ...prev,
                    financial_year: taxSettings.current_financial_year
                }));
            }

            // Fetch comparison
            const compData = await getTaxComparison(taxSettings.current_financial_year);
            setComparison(compData);
        } catch (error) {
            toast.error('Failed to fetch tax data');
        } finally {
            setLoading(false);
        }
    };

    const addInvestment = () => {
        setDeclaration(prev => ({
            ...prev,
            investments: [
                ...prev.investments,
                { category: '80C', name: '', amount: 0, remarks: '' }
            ]
        }));
    };

    const removeInvestment = (index) => {
        setDeclaration(prev => ({
            ...prev,
            investments: prev.investments.filter((_, i) => i !== index)
        }));
    };

    const updateInvestment = (index, field, value) => {
        setDeclaration(prev => {
            const newInvestments = [...prev.investments];
            newInvestments[index] = { ...newInvestments[index], [field]: value };
            return { ...prev, investments: newInvestments };
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await submitDeclaration(declaration);
            toast.success('Declaration submitted successfully');
            fetchInitialData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit declaration');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    const isWindowOpen = settings?.investment_declaration_open;
    const isApproved = declaration.status === 'APPROVED';
    const canEdit = isWindowOpen && !isApproved;

    return (
        <div className="space-y-6 animate-slide-up">
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Tax Declaration</h1>
                    <p className="text-muted-foreground">Declare your investments and choose tax regime for FY {settings?.current_financial_year}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    {isWindowOpen ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Declaration Window Open
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <AlertCircle className="h-3.5 w-3.5 mr-1" /> Declaration Window Closed
                        </Badge>
                    )}
                    {declaration.status && (
                        <Badge
                            variant={declaration.status === 'APPROVED' ? 'default' : declaration.status === 'REJECTED' ? 'destructive' : 'secondary'}
                        >
                            Status: {declaration.status}
                        </Badge>
                    )}
                </div>
            </div>

            {!isWindowOpen && !declaration.id && (
                <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-6">
                        <div className="flex gap-3 text-amber-800">
                            <AlertCircle className="h-5 w-5 flex-shrink-0" />
                            <p>The investment declaration window is currently closed by the administrator. You can only view your existing declarations if any.</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid gap-6">
                {comparison && (
                    <Card className="border-primary/20 bg-primary/5">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <ShieldCheck className="h-5 w-5 text-primary" />
                                    Regime Comparison
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => fetchInitialData()}
                                    className="h-8 text-xs"
                                >
                                    Refresh Comparison
                                </Button>
                            </CardTitle>
                            <CardDescription>Estimated annual tax liability for FY {settings?.current_financial_year}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className={`p-4 rounded-xl border-2 transition-all ${comparison.recommendation === 'NEW' ? 'bg-white border-primary border-dashed' : 'bg-slate-50 border-transparent'}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-lg">New Regime</h3>
                                        {comparison.recommendation === 'NEW' && (
                                            <Badge className="bg-primary hover:bg-primary">Recommended</Badge>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Annual Tax</p>
                                        <p className="text-2xl font-bold">{formatIndianCurrency(Math.round(comparison.new_regime.annual_tax))}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatIndianCurrency(Math.round(comparison.new_regime.monthly_tax))} / month
                                        </p>
                                    </div>
                                </div>

                                <div className={`p-4 rounded-xl border-2 transition-all ${comparison.recommendation === 'OLD' ? 'bg-white border-primary border-dashed' : 'bg-slate-50 border-transparent'}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-lg">Old Regime</h3>
                                        {comparison.recommendation === 'OLD' && (
                                            <Badge className="bg-primary hover:bg-primary">Recommended</Badge>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Annual Tax</p>
                                        <p className="text-2xl font-bold">{formatIndianCurrency(Math.round(comparison.old_regime.annual_tax))}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatIndianCurrency(Math.round(comparison.old_regime.monthly_tax))} / month
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 p-3 bg-white/50 rounded-lg text-sm border border-primary/10">
                                <p className="text-muted-foreground italic">
                                    * Calculation based on your current annual CTC of <strong>{formatIndianCurrency(comparison.annual_gross)}</strong>
                                    {comparison.old_regime.deductions > 0 && ` and total declared investments of ${formatIndianCurrency(comparison.old_regime.deductions)}.`}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle>Tax Regime Selection</CardTitle>
                        <CardDescription>Choose between the Old and New tax regimes</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="max-w-xs space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="regime">Select Regime</Label>
                                <Select
                                    disabled={!canEdit}
                                    value={declaration.regime}
                                    onValueChange={(val) => setDeclaration(prev => ({ ...prev, regime: val }))}
                                >
                                    <SelectTrigger id="regime">
                                        <SelectValue placeholder="Select regime" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="NEW">New Regime (Default)</SelectItem>
                                        <SelectItem value="OLD">Old Regime</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                                {declaration.regime === 'NEW' ? (
                                    <p>The New Regime offers lower tax rates but fewer deductions. <strong>Effective zero tax for income up to {formatIndianCurrency(1275000)}</strong> (including {formatIndianCurrency(75000)} standard deduction).</p>
                                ) : (
                                    <p>The Old Regime allows you to claim various deductions (80C, 80D, HRA, etc.) but has higher tax rates.</p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {declaration.regime === 'OLD' && (
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle>Investment Details</CardTitle>
                                <CardDescription>List your planned investments for the financial year</CardDescription>
                            </div>
                            {canEdit && (
                                <Button onClick={addInvestment} size="sm" variant="outline">
                                    <Plus className="h-4 w-4 mr-2" /> Add Investment
                                </Button>
                            )}
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Investment Name</TableHead>
                                        <TableHead>Amount (₹)</TableHead>
                                        <TableHead>Remarks</TableHead>
                                        {canEdit && <TableHead className="w-[50px]"></TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {declaration.investments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={canEdit ? 5 : 4} className="text-center py-8 text-muted-foreground">
                                                No investments added. Click "Add Investment" to start.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        declaration.investments.map((inv, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Select
                                                        disabled={!canEdit}
                                                        value={inv.category}
                                                        onValueChange={(val) => updateInvestment(index, 'category', val)}
                                                    >
                                                        <SelectTrigger className="w-[120px]">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="80C">80C</SelectItem>
                                                            <SelectItem value="80D">80D</SelectItem>
                                                            <SelectItem value="HRA">HRA</SelectItem>
                                                            <SelectItem value="Section 24(b)">Home Loan (24b)</SelectItem>
                                                            <SelectItem value="80CCD">NPS (80CCD)</SelectItem>
                                                            <SelectItem value="80G">80G</SelectItem>
                                                            <SelectItem value="Other">Other</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        disabled={!canEdit}
                                                        value={inv.name}
                                                        onChange={(e) => updateInvestment(index, 'name', e.target.value)}
                                                        placeholder="e.g. LIC, PPF, Rent"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        disabled={!canEdit}
                                                        type="number"
                                                        value={inv.amount}
                                                        onChange={(e) => updateInvestment(index, 'amount', parseFloat(e.target.value) || 0)}
                                                        className="text-right"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        disabled={!canEdit}
                                                        value={inv.remarks}
                                                        onChange={(e) => updateInvestment(index, 'remarks', e.target.value)}
                                                    />
                                                </TableCell>
                                                {canEdit && (
                                                    <TableCell>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => removeInvestment(index)}
                                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>

                            <div className="mt-6 space-y-2 border-t pt-4">
                                <div className="flex justify-end items-center gap-4 text-sm">
                                    <span className="text-muted-foreground">Total Declared: </span>
                                    <span className="font-semibold">
                                        {formatIndianCurrency(declaration.investments.reduce((sum, inv) => sum + (inv.amount || 0), 0))}
                                    </span>
                                </div>
                                <div className="flex justify-end items-center gap-4">
                                    <div className="text-right">
                                        <span className="text-sm text-muted-foreground">Eligible Deduction (Estimated): </span>
                                        <div className="font-bold text-xl text-primary">
                                            {(() => {
                                                const counts = declaration.investments.reduce((acc, inv) => {
                                                    const cat = inv.category;
                                                    acc[cat] = (acc[cat] || 0) + (inv.amount || 0);
                                                    return acc;
                                                }, {});

                                                const c_limit = 150000;
                                                const d_limit = 25000;
                                                const home_limit = 200000;
                                                const nps_limit = 50000;

                                                const eligible =
                                                    Math.min(counts['80C'] || 0, c_limit) +
                                                    Math.min(counts['80D'] || 0, d_limit) +
                                                    Math.min(counts['Section 24(b)'] || 0, home_limit) +
                                                    Math.min(counts['80CCD'] || 0, nps_limit) +
                                                    (counts['HRA'] || 0) +
                                                    (counts['80G'] || 0) +
                                                    (counts['Other'] || 0);

                                                return formatIndianCurrency(eligible);
                                            })()}
                                        </div>
                                        <p className="text-[10px] text-muted-foreground mt-1">
                                            *Section 80C capped at 1.5L, 80D at 25k, Home Loan Int. at 2L, NPS at 50k
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {canEdit && (
                    <div className="flex justify-end">
                        <Button
                            size="lg"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="px-8"
                        >
                            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Submit Declaration
                        </Button>
                    </div>
                )}

                {isApproved && (
                    <Card className="bg-green-50 border-green-200">
                        <CardContent className="pt-6">
                            <div className="flex gap-3 text-green-800">
                                <ShieldCheck className="h-5 w-5 flex-shrink-0" />
                                <p>Your investment declaration has been approved and cannot be modified. These values will be used for TDS calculation.</p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default MyTaxDeclaration;
